import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type ApiLoaderContextValue = {
  pendingCount: number;
  isLoading: boolean;
  start: () => void;
  stop: () => void;
  reset: () => void;
};

const ApiLoaderContext = createContext<ApiLoaderContextValue | undefined>(
  undefined,
);

export const ApiLoaderProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  const [pendingCount, setPendingCount] = useState(0);

  const start = useCallback(() => {
    setPendingCount((c) => c + 1);
  }, []);

  const stop = useCallback(() => {
    setPendingCount((c) => Math.max(0, c - 1));
  }, []);

  const reset = useCallback(() => {
    setPendingCount(0);
  }, []);

  const value = useMemo(
    () => ({
      pendingCount,
      isLoading: pendingCount > 0,
      start,
      stop,
      reset,
    }),
    [pendingCount, start, stop, reset],
  );

  return (
    <ApiLoaderContext.Provider value={value}>
      {children}
    </ApiLoaderContext.Provider>
  );
};

export const useApiLoader = (): ApiLoaderContextValue => {
  const ctx = useContext(ApiLoaderContext);
  if (!ctx) {
    throw new Error("useApiLoader must be used inside ApiLoaderProvider");
  }
  return ctx;
};
